import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, OctagonAlert, Info } from 'lucide-react';
import CTABanner from '../../components/CTABanner';
import WorkshopSearch from '../../components/werkstatt/WorkshopSearch';
import { usePageMeta, useStructuredData } from '../../lib/seo';
import { trackEvent } from '../../lib/analytics';
import { SITE_URL } from '../../config/productFacts';
import { WERKSTATT_BY_PATH } from '../../data/werkstatt';
import { ClusterHero, FaqSection, CrossLinkChips, breadcrumbSchema, faqSchema } from '../spritpreise/clusterShared';

const URGENCY_STYLES = {
  sofort: { icon: OctagonAlert, cls: 'border-red-200 bg-red-50 text-red-900', label: 'Nicht weiterfahren' },
  hoch: { icon: AlertTriangle, cls: 'border-amber-200 bg-amber-50 text-amber-900', label: 'Zeitnah prüfen lassen' },
  mittel: { icon: Info, cls: 'border-stone-200 bg-stone-50 text-stone-800', label: 'Beobachten & einplanen' },
};

const FUELX_VARIANTS = {
  default: 'Reparatur nötig? Mit der FuelX Card bekommst du bei Warnzeichen eine kostenlose Experten-Ersteinschätzung, kannst Kostenvoranschläge und Werkstattrechnungen prüfen lassen und behältst alle Autokosten zentral im Blick.',
  kosten: 'Unsicher, ob der Preis fair ist? FuelX Mitglieder lassen Kostenvoranschläge und Werkstattrechnungen vor der Freigabe von Experten prüfen — und verwalten alle Autokosten an einem Ort.',
  symptom: 'Unsicher, was dein Auto hat? Mit der FuelX Card beschreibst du das Symptom und erhältst eine kostenlose Experten-Ersteinschätzung — bevor du eine Werkstatt beauftragst.',
  marke: 'FuelX ist markenunabhängig: Mitglieder erhalten Experten-Ersteinschätzungen bei Warnzeichen, Rechnungsprüfung und Unterstützung bei der Werkstattauswahl — für jede Fahrzeugmarke.',
};

const KONFIGURATOR_PATH = '/checkout';

export default function WerkstattSeoPage() {
  const { pathname } = useLocation();
  const page = WERKSTATT_BY_PATH[pathname.replace(/\/$/, '')];

  usePageMeta(page.title, page.description, {
    canonical: `${SITE_URL}${page.path}`,
    noindex: !page.published,
  });

  const schema = useMemo(() => ({
    '@graph': [
      breadcrumbSchema([['FuelX', ''], ['Werkstatt', '/werkstatt'], [page.h1, page.path]]),
      faqSchema(page.faq),
    ],
  }), [page]);
  useStructuredData(schema, `werkstatt-${page.id}`);

  useEffect(() => {
    trackEvent('repair_page_view', {
      lp_id: page.id, primary_keyword: page.primary, cluster: page.cluster,
      page_type: page.pageType, priority: page.prio,
    });
  }, [page]);

  const urgency = page.urgency ? URGENCY_STYLES[page.urgency.level] : null;
  const UrgencyIcon = urgency?.icon;
  const fuelxText = FUELX_VARIANTS[page.group === 'kosten' ? 'kosten' : page.group === 'symptom' || page.group === 'diagnose' ? 'symptom' : page.group === 'marke' ? 'marke' : 'default'];

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid={`werkstatt-page-${page.id}`}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-16">
        <ClusterHero
          crumbs={[['FuelX', '/'], ['Werkstatt', '/werkstatt'], [page.crumb || page.h1, null]]}
          eyebrow={page.eyebrow}
          h1={page.h1}
          intro={page.answer}
          testId={`wk-${page.id}`}
        />

        {urgency && (
          <div className={`mt-8 flex items-start gap-3.5 rounded-2xl border px-5 py-4 ${urgency.cls}`} data-testid={`wk-${page.id}-urgency`}>
            <UrgencyIcon className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="text-[13.5px] font-bold">{urgency.label}</p>
              <p className="text-[13.5px] leading-relaxed mt-0.5">{page.urgency.text}</p>
            </div>
          </div>
        )}

        {page.search && (
          <div className="mt-10">
            <WorkshopSearch context={page.id} testId={`wk-${page.id}-search`} />
          </div>
        )}

        {page.sections.map((sec, i) => (
          <section key={sec.h2} className="mt-12 text-[15.5px] text-stone-600 leading-[1.75]" data-testid={`wk-${page.id}-section-${i}`}>
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-4">{sec.h2}</h2>
            {sec.text && <p className="mb-3">{sec.text}</p>}
            {sec.items && (
              <ul className="list-disc pl-5 space-y-2">
                {sec.items.map((it) => (
                  <li key={it.slice(0, 40)} dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.+?)\*\*/g, '<strong class="text-stone-900">$1</strong>') }} />
                ))}
              </ul>
            )}
          </section>
        ))}

        {['reparatur', 'kosten', 'service'].includes(page.pageType) && (
          <p className="mt-8 rounded-xl bg-stone-50 border border-stone-200 px-5 py-4 text-[13.5px] text-stone-600" data-testid={`wk-${page.id}-cost-note`}>
            <strong className="text-stone-900">Hinweis zu Kosten:</strong> Die tatsächlichen Kosten hängen vom Fahrzeugmodell, dem Umfang des Schadens, den benötigten Ersatzteilen und dem Arbeitsaufwand der Werkstatt ab. Lass dir vor der Reparatur immer einen schriftlichen Kostenvoranschlag geben.
          </p>
        )}

        {/* FuelX-Bereich — nach der eigentlichen Information */}
        <section className="mt-12 rounded-2xl bg-stone-950 p-7 sm:p-9 text-white" data-testid={`wk-${page.id}-fuelx`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400 mb-3">FuelX für Autofahrer</p>
          <h2 className="font-display text-xl sm:text-2xl tracking-[-0.02em] mb-3">Mehr Transparenz bei Werkstatt- und Autokosten</h2>
          <p className="text-[14.5px] text-stone-300 leading-relaxed">{fuelxText}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/fuelx-card"
              onClick={() => trackEvent('fuelx_card_click', { lp_id: page.id, cluster: page.cluster })}
              className="inline-flex items-center rounded-full bg-emerald-400 px-6 py-2.5 text-[13.5px] font-semibold text-emerald-950 hover:bg-emerald-300 transition-colors"
              data-testid={`wk-${page.id}-fuelx-card-link`}
            >
              FuelX Card entdecken
            </Link>
            <Link
              to={KONFIGURATOR_PATH}
              onClick={() => trackEvent('fuelx_configurator_click', { lp_id: page.id, cluster: page.cluster })}
              className="inline-flex items-center rounded-full border border-stone-600 px-6 py-2.5 text-[13.5px] font-semibold text-stone-200 hover:border-emerald-400 hover:text-emerald-300 transition-colors"
              data-testid={`wk-${page.id}-configurator-link`}
            >
              Zum Konfigurator
            </Link>
          </div>
        </section>

        <FaqSection title={page.faqTitle} items={page.faq} testId={`wk-${page.id}-faq`} />

        <CrossLinkChips links={[...page.related, ['Werkstatt-Übersicht', '/werkstatt']]} testId={`wk-${page.id}-related`} />

        <p className="mt-10 text-[11.5px] text-stone-400" data-testid={`wk-${page.id}-source`}>
          Allgemeine Informationen, keine Ferndiagnose — bei sicherheitsrelevanten Symptomen immer einen Fachbetrieb prüfen lassen.
          {' '}Themenquelle: <a href={page.source.url} target="_blank" rel="nofollow noopener noreferrer" className="underline underline-offset-2 hover:text-stone-600">{page.source.label}</a> · Stand: {page.updated}
        </p>
      </div>

      <CTABanner
        variant="light"
        headline={<>Werkstattkosten im Griff.<br /><span className="text-stone-400">Mit System.</span></>}
        text="Die FuelX Card bündelt Fehlerdiagnose, Rechnungsprüfung und 37 Auto-Leistungen — ab 99,90 €/Jahr."
        buttonText="FuelX Card entdecken"
        buttonTo="/fuelx-card"
      />
    </div>
  );
}
